/**
 * features/statements/StatementsView.tsx
 * Pick a client and (optionally) a period to see a running account
 * ledger, then download or email the statement PDF.
 */
import { useMemo, useState } from 'react';
import { FileText, Download, Mail } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { useClients } from '@/features/clients/useClients';
import { useInvoices } from '@/features/invoices/useInvoices';
import { usePayments } from '@/features/payments/usePayments';
import { useCreditNotes } from '@/features/credit/useCreditNotes';
import { clientSnapshot } from '@/features/invoices/invoicesWrite';
import { buildStatement } from './statementData';
import { downloadStatementPdf, statementPdfBase64, statementFileName, type StatementMeta } from '@/lib/statementPdf';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import { formatPula } from '@/lib/money';
import { Button, Card, Field, Select, Input, Spinner } from '@/components/ui/primitives';

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function StatementsView() {
  const { settings } = useSettings();
  const { clients, loading: lc } = useClients();
  const { invoices } = useInvoices();
  const { payments } = usePayments();
  const { creditNotes } = useCreditNotes();

  const [clientId, setClientId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [emailing, setEmailing] = useState(false);

  const client = clients.find((c) => c.id === clientId);
  const result = useMemo(
    () => (clientId ? buildStatement(clientId, invoices, payments, creditNotes, from || undefined, to || undefined) : null),
    [clientId, invoices, payments, creditNotes, from, to],
  );

  const meta: StatementMeta | null = client
    ? { client: clientSnapshot(client), from: from || undefined, to: to || undefined, generatedOn: new Date().toISOString().slice(0, 10) }
    : null;

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Client">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select a client…</option>
              {clients.filter((c) => c.active).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From" hint="Optional">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To" hint="Optional">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {lc ? (
        <Spinner label="Loading…" />
      ) : !result || !meta ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <FileText size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Choose a client to generate their account statement.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-black/5 px-4 py-3 dark:border-white/10">
            <div className="font-semibold text-primary dark:text-gray-100">{client?.name}</div>
            {settings && (
              <div className="flex gap-1">
                <Button variant="secondary" icon={<Download size={15} />} onClick={() => downloadStatementPdf(result, meta, settings)}>
                  Download
                </Button>
                <Button variant="secondary" icon={<Mail size={15} />} onClick={() => setEmailing(true)} disabled={!client?.email}>
                  Email
                </Button>
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Ref</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 text-right font-semibold">Debit</th>
                <th className="px-4 py-3 text-right font-semibold">Credit</th>
                <th className="px-4 py-3 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-black/5 dark:border-white/5">
                <td className="px-4 py-2.5 text-primaryLight dark:text-gray-400" colSpan={5}>
                  Opening balance
                </td>
                <td className="px-4 py-2.5 text-right font-medium text-primary dark:text-gray-200">{formatPula(result.opening)}</td>
              </tr>
              {result.entries.map((e, i) => (
                <tr key={i} className="border-b border-black/5 last:border-0 dark:border-white/5">
                  <td className="px-4 py-2.5 text-primaryLight dark:text-gray-400">{fmtDate(e.date)}</td>
                  <td className="px-4 py-2.5 text-primary dark:text-gray-200">{e.ref}</td>
                  <td className="px-4 py-2.5 text-primary dark:text-gray-200">{e.description}</td>
                  <td className="px-4 py-2.5 text-right text-primary dark:text-gray-200">{e.debit ? formatPula(e.debit) : ''}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 dark:text-green-400">{e.credit ? formatPula(e.credit) : ''}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-primary dark:text-gray-100">{formatPula(e.balance)}</td>
                </tr>
              ))}
              <tr className="bg-brandSoft/50 dark:bg-white/5">
                <td className="px-4 py-3 font-bold text-primary dark:text-gray-100" colSpan={5}>
                  Balance due
                </td>
                <td className="px-4 py-3 text-right text-base font-bold text-brand">{formatPula(result.closing)}</td>
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {emailing && client && meta && settings && (
        <SendDocumentModal
          heading={`Email statement to ${client.name}`}
          to={client.email ?? ''}
          subject={`Account statement — Acepsle`}
          message={`Dear ${client.name},\n\nPlease find attached your account statement. The balance due is ${formatPula(result!.closing)}.\n\nKind regards,\nAcepsle Proprietary Limited`}
          filename={statementFileName(meta.client, meta.to)}
          contentBase64={statementPdfBase64(result!, meta, settings)}
          relatedEntityType="statement"
          relatedEntityId={clientId}
          onClose={() => setEmailing(false)}
        />
      )}
    </div>
  );
}
